import React, { useState, useEffect, useRef } from 'react';
import { 
  collection, 
  doc, 
  getDocs,
  query,
  limit, 
  addDoc, 
  updateDoc, 
  deleteDoc 
} from 'firebase/firestore';
import { useNavigate, useLocation } from 'react-router-dom';
import { db } from '../firebase/firebase';
import { 
  Container, 
  Card, 
  Button, 
  Modal, 
  Form, 
  ProgressBar 
} from 'react-bootstrap';
import { 
  CheckCircle, 
  Circle, 
  Edit, 
  Trash2, 
  Plus 
} from 'lucide-react';

// Utility function to generate predefined tasks
const generatePredefinedTasks = (startDate) => {
  const start = new Date(startDate);

  const createTask = (title, daysBefore = null, daysAfter = null) => {
    const dueDate = daysBefore 
      ? new Date(start.getTime() - daysBefore * 24 * 60 * 60 * 1000)
      : new Date(start.getTime() + daysAfter * 24 * 60 * 60 * 1000);

    return {
      title,
      description: daysBefore 
        ? `Aufgabe ${daysBefore} Tage vor Lager` 
        : `Aufgabe ${daysAfter} Tage nach Lager`,
      dueDate: dueDate.toISOString(),
      completed: false,
      daysBefore,
      daysAfter
    };
  };

  return [
    // 365 Tage vor dem Lager (nur verbleibende Aufgaben)
    // createTask('Leitungsteam zusammenstellen', 365) wurde entfernt
    createTask('Lagerhaus bzw. Lagerplatz suchen, reservieren und organisieren', 365),
    // createTask('Mietvertrag unterschreiben', 365) wurde entfernt

    // 120 Tage vor dem Lager
    createTask('Formulare für den Jugendurlaub ausfüllen', 120),
    createTask('Motto und roter Faden definieren', 120),
    createTask('Erstes Budget erstellen', 120),

    // 90 Tage vor dem Lager
    createTask('Lager auf Ecamp3 erstellen und Grobprogramm planen / AL\'s in eCamp einladen', 90),
    createTask('Küche organisieren', 90),

    // 60 Tage vor dem Lager
    createTask('Grobprogramm an Coach versenden', 60),
    createTask('Auf MiData alle Lagerdaten und Infos vervollständigen', 60),
    createTask('Ersten Höck mit den Leitenden organisieren (Blockplanung, Rollen, Ämtli verteilen)', 60),
    createTask('Lageranmeldung an Teilnehmende versenden', 60),
    createTask('Sicherheitskonzept erstellen', 60),
    createTask('J+S Material bestellen', 60),
    createTask('Landeskarten bestellen', 60),
    createTask('Ggf. Elternabend organisieren', 60),

    // 40 Tage vor dem Lager
    createTask('Definitives Lagerbudget erstellen', 40),
    createTask('Einwohnergemeinde informieren', 40),
    createTask('Leitungspersonen und Küche in MiData erfassen', 40),
    createTask('Detailprogramm an Coach abgeben', 40),

    // 30 Tage vor dem Lager
    createTask('Lageranmeldung verschicken', 30),

    // Ersetze "Recken" durch "Rekognoszieren"
    createTask('Rekken', 30),
    createTask('Lagerauto organisieren', 30),
    createTask('Materialliste erstellen', 30),
    createTask('Materialtransport organisieren', 30),
    createTask('Lagerregel bestimmen', 30),
    createTask('Absprache mit der Küche', 30),
    // Hinzufügen: Anmeldeschluss für Lageranmeldung
    createTask('Anmeldeschluss für Lageranmeldung', 30),

    // 20 Tage vor dem Lager
    createTask('Höck mit den Leitenden (Finale Besprechung) organisieren', 20),

    // 14 Tage vor dem Lager
    // Bestehende Aufgaben
    createTask('SBB-Gruppenbillett organisieren', 14),
    createTask('Detailinfos an Teilnehmende versenden', 14),
    // Hinzufügen: Programmbesprechung mit Coach
    createTask('Programmbesprechung mit Coach', 14),

    // 60 Tage nach dem Lager
    // Ursprüngliche Aufgabe wurde entfernt
    // Hinzufügen: Nachlageraufgaben
    createTask('Auswertungshöck', null, 14),
    createTask('Abrechnung', null, 21),
    createTask('Lagerrückblick', null, 28)
  ];
};

// TodoListItem component remains the same
const TodoListItem = ({ task, onToggleComplete, onEdit, onDelete }) => {
  return (
    <Card className={`mb-2 ${task.completed ? 'bg-light' : ''}`}>
      <Card.Body className="d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center">
          <Button 
            variant="link" 
            className="p-0 me-3"
            onClick={() => onToggleComplete(task.id)}
          >
            {task.completed ? (
              <CheckCircle className="text-success" />
            ) : (
              <Circle className="text-muted" />
            )}
          </Button>
          <div>
            <h6 className={`mb-1 ${task.completed ? 'text-muted text-decoration-line-through' : ''}`}>
              {task.title}
            </h6>
            {task.description && (
              <small className="text-muted d-block">
                {task.description}
              </small>
            )}
            {task.dueDate && (
              <small className="text-muted d-block">
                Fällig am: {new Date(task.dueDate).toLocaleDateString()}
              </small>
            )}
            {task.assignedTo && (
              <small className="text-muted d-block">
                Verantwortlich: {task.assignedTo}
              </small>
            )}
          </div>
        </div>
        <div>
          <Button 
            variant="link" 
            className="p-0 me-2" 
            onClick={() => onEdit(task)}
          >
            <Edit size={18} className="text-primary" />
          </Button>
          <Button 
            variant="link" 
            className="p-0" 
            onClick={() => onDelete(task.id)}
          >
            <Trash2 size={18} className="text-danger" />
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};

const InteractiveTodoList = ({ 
  lagerId, 
  lagerStartDate = null,
  prePopulateTasks = true 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tasks, setTasks] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [filter, setFilter] = useState('all');
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchAttemptRef = useRef(false);

  // Fetch tasks for specific lager
  useEffect(() => {
    let isMounted = true;
    
    const fetchTasks = async () => {
      // Prevent double fetching in StrictMode
      if (fetchAttemptRef.current) {
        console.log('Skipping duplicate fetch attempt');
        return;
      }
      fetchAttemptRef.current = true;

      if (!lagerId) {
        console.log('No lagerId provided');
        setIsLoading(false);
        return;
      }
      
      // Don't fetch if we're already initialized
      if (isInitialized) {
        console.log('Already initialized, skipping fetch');
        return;
      }

      setIsLoading(true);
      setError(null);
      
      try {
        console.log('Fetching tasks for lagerId:', lagerId);
        const tasksCollection = collection(db, 'lager', lagerId, 'todoList');
        console.log('Collection reference created');
        
        const tasksSnapshot = await getDocs(tasksCollection);
        console.log('Got tasks snapshot:', tasksSnapshot.size, 'documents');
        
        // If component is unmounted, don't proceed
        if (!isMounted) return;

        const tasksList = tasksSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        console.log('Mapped tasks:', tasksList.length, 'tasks');

        if (tasksList.length === 0 && prePopulateTasks && lagerStartDate) {
          console.log('Pre-populating tasks...');
          const predefinedTasks = generatePredefinedTasks(lagerStartDate);
          
          if (!predefinedTasks || !Array.isArray(predefinedTasks)) {
            console.error('Failed to generate predefined tasks');
            if (isMounted) {
              setIsInitialized(true);
              setIsLoading(false);
            }
            return;
          }

          // Save predefined tasks to Firestore
          const newTaskPromises = predefinedTasks.map(task => 
            addDoc(tasksCollection, task)
          );

          const newTaskRefs = await Promise.all(newTaskPromises);
          
          if (!isMounted) return;

          const newTasksList = predefinedTasks.map((task, index) => ({
            id: newTaskRefs[index].id,
            ...task
          }));

          console.log('Added predefined tasks:', newTasksList.length, 'tasks');
          setTasks(newTasksList);
        } else {
          console.log('Setting existing tasks');
          setTasks(tasksList);
        }
        
      } catch (error) {
        console.error('Error fetching tasks:', error);
        if (isMounted) {
          setError(error.message);
        }
      } finally {
        if (isMounted) {
          setIsInitialized(true);
          setIsLoading(false);
        }
      }
    };

    fetchTasks();

    // Cleanup function
    return () => {
      isMounted = false;
      fetchAttemptRef.current = false;  // Reset on cleanup
    };
  }, [lagerId, lagerStartDate, prePopulateTasks]);

  // Calculate progress
  const completedTasks = tasks.filter(task => task.completed).length;
  const progressPercentage = tasks.length > 0 
    ? Math.round((completedTasks / tasks.length) * 100) 
    : 0;

  // Filter and sort tasks
  const filteredTasks = tasks
    .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
    .filter(task => {
      if (filter === 'completed') return task.completed;
      if (filter === 'pending') return !task.completed;
      return true;
    });

  // Handler functions remain the same
  const handleToggleComplete = async (taskId) => {
    const taskToUpdate = tasks.find(task => task.id === taskId);
    
    try {
      const taskDoc = doc(db, 'lager', lagerId, 'todoList', taskId);
      await updateDoc(taskDoc, {
        completed: !taskToUpdate.completed,
        completedAt: !taskToUpdate.completed ? new Date().toISOString() : null
      });

      setTasks(tasks.map(task => 
        task.id === taskId 
          ? { ...task, completed: !task.completed } 
          : task
      ));
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleOpenModal = (task = null) => {
    setCurrentTask(task);
    setShowModal(true);
  };

  const handleSaveTask = async (e) => {
    e.preventDefault();
    
    try {
      const taskData = {
        title: e.target.title.value,
        description: e.target.description.value,
        assignedTo: e.target.assignedTo.value,
        dueDate: e.target.dueDate.value,
        completed: false
      };

      if (currentTask) {
        const taskDoc = doc(db, 'lager', lagerId, 'todoList', currentTask.id);
        await updateDoc(taskDoc, taskData);

        setTasks(tasks.map(task => 
          task.id === currentTask.id 
            ? { ...task, ...taskData } 
            : task
        ));
      } else {
        const tasksCollection = collection(db, 'lager', lagerId, 'todoList');
        const docRef = await addDoc(tasksCollection, taskData);
        
        setTasks([...tasks, { id: docRef.id, ...taskData }]);
      }

      setShowModal(false);
      setCurrentTask(null);
    } catch (error) {
      console.error('Error saving task:', error);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (window.confirm('Möchten Sie diese Aufgabe wirklich löschen?')) {
      try {
        const taskDoc = doc(db, 'lager', lagerId, 'todoList', taskId);
        await deleteDoc(taskDoc);

        setTasks(tasks.filter(task => task.id !== taskId));
      } catch (error) {
        console.error('Error deleting task:', error);
      }
    }
  };

  // Return JSX remains the same
  return (
    <Container>
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h4>Todo-Liste</h4>
          <div className="d-flex align-items-center">
            <Button 
              variant="outline-primary" 
              className="me-2"
              onClick={() => handleOpenModal()}
              disabled={isLoading}
            >
              <Plus size={16} className="me-1" /> Neue Aufgabe
            </Button>
            <Form.Select 
              size="sm" 
              style={{width: '120px'}}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              disabled={isLoading}
            >
              <option value="all">Alle</option>
              <option value="pending">Offen</option>
              <option value="completed">Erledigt</option>
            </Form.Select>
          </div>
        </Card.Header>
        <Card.Body>
          {isLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Lade Aufgaben...</p>
            </div>
          ) : error ? (
            <div className="alert alert-danger" role="alert">
              Fehler beim Laden der Aufgaben: {error}
            </div>
          ) : tasks.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted">Keine Aufgaben vorhanden</p>
            </div>
          ) : (
            <>
              <ProgressBar 
                now={progressPercentage} 
                label={`${progressPercentage}%`} 
                variant={progressPercentage === 100 ? 'success' : 'primary'}
                className="mb-3"
              />
              
              {filteredTasks.map(task => (
                <TodoListItem
                  key={task.id}
                  task={task}
                  onToggleComplete={handleToggleComplete}
                  onEdit={() => handleOpenModal(task)}
                  onDelete={handleDeleteTask}
                />
              ))}
            </>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {currentTask ? 'Aufgabe bearbeiten' : 'Neue Aufgabe'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSaveTask}>
            <Form.Group className="mb-3">
              <Form.Label>Titel</Form.Label>
              <Form.Control
                type="text"
                name="title"
                defaultValue={currentTask?.title || ''}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Beschreibung</Form.Label>
              <Form.Control
                as="textarea"
                name="description"
                defaultValue={currentTask?.description || ''}
                rows={3}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Verantwortliche Person</Form.Label>
              <Form.Control
                type="text"
                name="assignedTo"
                defaultValue={currentTask?.assignedTo || ''}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Fälligkeitsdatum</Form.Label>
              <Form.Control
                type="date"
                name="dueDate"
                defaultValue={currentTask?.dueDate ? 
                  new Date(currentTask.dueDate).toISOString().split('T')[0] : 
                  ''
                }
              />
            </Form.Group>
            <div className="d-flex justify-content-end">
              <Button variant="secondary" className="me-2" onClick={() => setShowModal(false)}>
                Abbrechen
              </Button>
              <Button variant="primary" type="submit">
                Speichern
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default InteractiveTodoList;